import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { 
  TrendingUp,
  Briefcase,
  PlayCircle,
  PauseCircle,
  Clock,
  CheckCircle,
  Award,
  XCircle,
  AlertTriangle,
  Star,
  Info,
  Calendar,
  RefreshCw,
  Bell
} from 'lucide-react';

// Smart Tooltip with auto-positioning
const MetricTooltip = ({ content }) => {
  const [position, setPosition] = React.useState('top');
  const iconRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      if (rect.right > viewportWidth - 250) {
        setPosition('left');
      } else if (rect.left < 250) {
        setPosition('right');
      } else {
        setPosition('top');
      }
    }
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-slate-800',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-slate-800',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-slate-800'
  };

  return (
    <div className="group relative inline-block ml-1" onMouseEnter={handleMouseEnter} ref={iconRef}>
      <Info size={12} className="text-slate-400 cursor-help hover:text-primary transition-colors" />
      <div className={`absolute px-2.5 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-48 z-50 shadow-xl ${positionClasses[position]}`}>
        {content}
        <div className={`absolute border-4 border-transparent ${arrowClasses[position]}`}></div>
      </div>
    </div>
  );
};

// Period Filter Component
const PeriodFilter = ({ selectedPeriod, onPeriodChange }) => {
  const periods = [
    { key: 7, label: '7 Days' },
    { key: 15, label: '15 Days' },
    { key: 30, label: '30 Days' },
    { key: 90, label: '90 Days' },
    { key: 180, label: '180 Days' },
    { key: 365, label: '365 Days' },
  ];

  return (
    <div className="flex items-center gap-2" data-testid="period-filter">
      <div className="flex items-center gap-1.5 text-slate-500 text-sm">
        <Calendar size={16} />
        <span>Period:</span>
      </div>
      <div className="flex gap-1">
        {periods.map(p => (
          <button
            key={p.key}
            onClick={() => onPeriodChange(p.key)}
            data-testid={`period-${p.key}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
              selectedPeriod === p.key
                ? 'bg-primary text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Metric tooltips
const METRIC_TOOLTIPS = {
  success_rate: "Visas Approved / Total Decided Cases. Shows your approval success rate.",
  total_cases: "All cases ever assigned to you.",
  active_cases: "Cases currently in progress requiring attention.",
  suspended_cases: "Cases suspended due to non-payment of balance.",
  expired_cases: "Cases expired due to document submission timeout.",
  completed_cases: "Cases with final outcome (Approved/Rejected/Closed).",
  visas_approved: "Visa applications successfully approved.",
  visas_rejected: "Visa applications rejected.",
  complaints: "Client complaints on your assigned cases.",
  csat_score: "Customer Satisfaction score from client feedback."
};

export default function CaseOfficerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  useEffect(() => {
    fetchMetrics();
  }, [selectedPeriod]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/officer/dashboard-metrics?days=${selectedPeriod}`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      toast.error('Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleMetricClick = (metricType) => {
    const routes = {
      total_cases: '/officer/cases',
      active_cases: '/officer/cases?filter=active',
      suspended_cases: '/officer/cases?filter=suspended',
      expired_cases: '/officer/cases?filter=expired',
      completed_cases: '/officer/cases?filter=completed',
      visas_approved: '/officer/cases?filter=approved',
      visas_rejected: '/officer/cases?filter=rejected',
      complaints: '/officer/complaints',
    };
    if (routes[metricType]) navigate(routes[metricType]);
  };

  // Metric cards configuration - using consistent primary theme color
  const metricCards = [
    {
      key: 'success_rate',
      label: 'My Success Rate',
      value: metrics?.success_rate != null ? `${metrics.success_rate}%` : '0%',
      icon: TrendingUp,
      tooltip: METRIC_TOOLTIPS.success_rate,
      clickable: false,
      subtext: metrics?.total_decided > 0 ? `${metrics.visas_approved}/${metrics.total_decided} decided` : 'No decided cases',
      accent: metrics?.success_rate >= 75 ? 'emerald' : metrics?.success_rate >= 50 ? 'amber' : 'red'
    },
    {
      key: 'total_cases',
      label: 'My Total Cases',
      value: metrics?.total_cases || 0,
      icon: Briefcase,
      tooltip: METRIC_TOOLTIPS.total_cases,
      clickable: true
    },
    {
      key: 'active_cases',
      label: 'Active Cases',
      value: metrics?.active_cases || 0,
      icon: PlayCircle,
      tooltip: METRIC_TOOLTIPS.active_cases,
      clickable: true
    },
    {
      key: 'suspended_cases',
      label: 'Suspended',
      value: metrics?.suspended_cases || 0,
      icon: PauseCircle,
      tooltip: METRIC_TOOLTIPS.suspended_cases,
      clickable: true,
      highlight: metrics?.suspended_cases > 0
    },
    {
      key: 'expired_cases',
      label: 'Expired',
      value: metrics?.expired_cases || 0,
      icon: Clock,
      tooltip: METRIC_TOOLTIPS.expired_cases,
      clickable: true
    },
    {
      key: 'completed_cases',
      label: 'Completed',
      value: metrics?.completed_cases || 0,
      icon: CheckCircle,
      tooltip: METRIC_TOOLTIPS.completed_cases,
      clickable: true
    },
    {
      key: 'visas_approved',
      label: 'Visas Approved',
      value: metrics?.visas_approved || 0,
      icon: Award,
      tooltip: METRIC_TOOLTIPS.visas_approved,
      clickable: true,
      accent: 'emerald'
    },
    {
      key: 'visas_rejected',
      label: 'Visas Rejected',
      value: metrics?.visas_rejected || 0,
      icon: XCircle,
      tooltip: METRIC_TOOLTIPS.visas_rejected,
      clickable: true,
      accent: metrics?.visas_rejected > 0 ? 'red' : null
    },
    {
      key: 'complaints',
      label: 'Complaints',
      value: metrics?.complaints || 0,
      icon: AlertTriangle,
      tooltip: METRIC_TOOLTIPS.complaints,
      clickable: true,
      subtext: metrics?.open_complaints > 0 ? `${metrics.open_complaints} open` : null,
      highlight: metrics?.open_complaints > 0
    },
    {
      key: 'csat_score',
      label: 'My CSAT Score',
      value: metrics?.csat_score != null ? `${metrics.csat_score}%` : '--',
      icon: Star,
      tooltip: METRIC_TOOLTIPS.csat_score,
      clickable: false,
      subtext: metrics?.total_ratings > 0 ? `${metrics.total_ratings} ratings` : 'No ratings',
      accent: metrics?.csat_score >= 85 ? 'emerald' : metrics?.csat_score >= 70 ? 'amber' : null
    }
  ];

  if (loading && !metrics) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content bg-slate-50" data-testid="officer-dashboard">
        {/* Header with Profile Menu */}
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="dashboard-title">
              Welcome, {user?.full_name}
            </h1>
            <p className="text-slate-500 text-sm">Your performance overview</p>
          </div>
          <div className="flex items-center gap-3">
            <PeriodFilter selectedPeriod={selectedPeriod} onPeriodChange={setSelectedPeriod} />
            <button
              onClick={fetchMetrics}
              disabled={loading}
              className="p-2 text-slate-500 hover:text-primary hover:bg-slate-100 rounded-lg transition-all"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <div className="w-px h-8 bg-slate-200"></div>

          </div>
        </div>

        {/* Metrics Grid - Compact 5-column layout */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="metrics-grid">
          {metricCards.map((metric) => {
            const Icon = metric.icon;
            const accentColors = {
              emerald: 'text-emerald-600',
              amber: 'text-amber-600',
              red: 'text-red-600'
            };
            
            return (
              <div
                key={metric.key}
                data-testid={`metric-${metric.key}`}
                onClick={() => metric.clickable && handleMetricClick(metric.key)}
                className={`bg-white rounded-xl border p-3.5 transition-all duration-200 ${
                  metric.clickable 
                    ? 'cursor-pointer hover:shadow-md hover:border-teal-300 hover:-translate-y-0.5' 
                    : ''
                } ${metric.highlight ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
                    <Icon className="text-teal-600" size={16} />
                  </div>
                  <MetricTooltip content={metric.tooltip} />
                </div>
                <p className="text-xs font-medium text-slate-500 mb-0.5">{metric.label}</p>
                <p className={`text-xl font-bold ${metric.accent ? accentColors[metric.accent] : 'text-slate-900'}`}>
                  {metric.value}
                </p>
                {metric.subtext && (
                  <p className={`text-xs mt-0.5 ${metric.highlight ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>
                    {metric.subtext}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Alerts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          {metrics?.pending_documents > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pending-docs-alert">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800 text-sm">
                      {metrics.pending_documents} Document{metrics.pending_documents > 1 ? 's' : ''} Pending
                    </h3>
                    <p className="text-xs text-amber-600">Awaiting your review</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/officer/documents')}
                  className="px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 transition-all"
                  data-testid="review-docs-btn"
                >
                  Review
                </button>
              </div>
            </div>
          )}

          {metrics?.urgent_cases > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="urgent-cases-alert">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="text-red-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-800 text-sm">
                      {metrics.urgent_cases} Urgent Case{metrics.urgent_cases > 1 ? 's' : ''}
                    </h3>
                    <p className="text-xs text-red-600">Pending docs &gt; 10 days</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/officer/cases?filter=urgent')}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-all"
                  data-testid="view-urgent-btn"
                >
                  View
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="quick-actions">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: Briefcase, label: 'All Cases', desc: 'View all assigned', path: '/officer/cases' },
              { icon: CheckCircle, label: 'Documents', desc: 'Review uploads', path: '/officer/documents' },
              { icon: PlayCircle, label: 'Active', desc: 'Cases in progress', path: '/officer/cases?filter=active' },
              { icon: AlertTriangle, label: 'Complaints', desc: 'Client feedback', path: '/officer/complaints' },
            ].map((action, idx) => (
              <button
                key={idx}
                onClick={() => navigate(action.path)}
                className="p-3 bg-slate-50 rounded-lg hover:bg-primary/5 hover:border-primary/20 border border-transparent transition-all text-left group"
                data-testid={`quick-action-${idx}`}
              >
                <action.icon className="text-teal-600 mb-2 group-hover:scale-110 transition-transform" size={20} />
                <p className="font-medium text-slate-900 text-sm">{action.label}</p>
                <p className="text-xs text-slate-500">{action.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
